/**
 * Pushmeldingen op dit apparaat: de service worker registreren, en aan- en
 * afmelden bij de pushdienst van de browser.
 *
 * De service worker (`public/sw.js`) doet niets anders dan meldingen tonen en een
 * tik erop naar de juiste pagina sturen. Geen offline-cache: MamiMap zonder
 * verbinding heeft niets te laten zien.
 *
 * Op een iPhone werkt push alleen in de webapp die op het beginscherm staat
 * (iOS 16.4 en later): in Safari zelf bestaat `PushManager` niet. `ondersteund`
 * zegt dat, en Instellingen legt dan uit hoe je hem op het beginscherm zet.
 */

export type PushOndersteuning = 'ja' | 'beginscherm' | 'nee';

export function pushOndersteuning(): PushOndersteuning {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return 'nee';
  if ('PushManager' in window && 'Notification' in window) return 'ja';
  /* Safari op iOS zonder beginscherm: geen PushManager, wel een service worker. */
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ? 'beginscherm' : 'nee';
}

/** Eén keer bij het opstarten; een mislukte registratie laat de app verder ongemoeid. */
export function registreerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
  });
}

async function registratie(): Promise<ServiceWorkerRegistration> {
  const bestaand = await navigator.serviceWorker.getRegistration('/');
  if (bestaand) return bestaand;
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

export async function huidigAbonnement(): Promise<PushSubscription | null> {
  if (pushOndersteuning() !== 'ja') return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/**
 * Toestemming vragen en abonneren. Moet vanuit een klik komen: browsers weigeren
 * een toestemmingsvraag die niet door de gebruiker is uitgelokt.
 */
export async function abonneer(publiekeSleutel: string): Promise<PushSubscription> {
  const toestemming = await Notification.requestPermission();
  if (toestemming !== 'granted') {
    throw new Error(
      toestemming === 'denied'
        ? 'Meldingen staan voor MamiMap uit in de browser. Zet ze daar aan en probeer opnieuw.'
        : 'Geen toestemming gekregen.',
    );
  }
  const reg = await registratie();
  await navigator.serviceWorker.ready;
  const bestaand = await reg.pushManager.getSubscription();
  if (bestaand) return bestaand;
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlNaarBytes(publiekeSleutel),
  });
}

export function alsJson(abonnement: PushSubscription): {
  endpoint: string;
  keys: { p256dh: string; auth: string };
} {
  const json = abonnement.toJSON();
  return {
    endpoint: abonnement.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
  };
}

function base64UrlNaarBytes(tekst: string): Uint8Array<ArrayBuffer> {
  const opgevuld = `${tekst}${'='.repeat((4 - (tekst.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const ruw = atob(opgevuld);
  const bytes = new Uint8Array(new ArrayBuffer(ruw.length));
  for (let i = 0; i < ruw.length; i += 1) bytes[i] = ruw.charCodeAt(i);
  return bytes;
}
